// Wave 20.33 — /embed/wind
//
// Bare canvas page designed to be loaded inside a react-native-webview
// on mobile. Renders the exact same windy.com-style particle sim we
// use on desktop (fade layer + trails + non-linear speed calibration),
// but with no app chrome — no MUI theme, no navigation, no guards —
// so the parent app controls the framing and interactivity.
//
// The mobile Mapbox MapView renders underneath; the WebView is
// transparent and pointer-events: none, so pinch/pan still land on
// the map. The mobile side postMessages `{ bbox, size, jwt }` to
// this page whenever the camera settles; this page rebuilds the
// wind PNG URL and reloads the field.

import React, { useCallback, useEffect, useRef } from 'react';

// Wave 20.49dh — Weather goes through the api proxy.
const API_BASE = process.env.REACT_APP_API || 'https://api.aero.chat';
const WEATHER_API = `${API_BASE}/weather`;
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

const PARTICLE_COUNT = 800;
const MAX_AGE = 90;
const SPEED_EXPONENT = 1.35;
const SPEED_BASE = 0.11;
const FADE_ALPHA = 0.045;
const WIND_MAX = 51;

interface Field {
  width: number;
  height: number;
  bbox: [number, number, number, number];
  imgData: ImageData;
}

async function decodeField(url: string, bbox: [number, number, number, number], jwt: string): Promise<Field | null> {
  try {
    const res = await fetch(url, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    const bmp = await createImageBitmap(blob);
    const c = document.createElement('canvas');
    c.width = bmp.width;
    c.height = bmp.height;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0);
    return { width: bmp.width, height: bmp.height, bbox, imgData: ctx.getImageData(0, 0, bmp.width, bmp.height) };
  } catch {
    return null;
  }
}

function sampleField(field: Field, lng: number, lat: number): [number, number] | null {
  const [w, s, e, n] = field.bbox;
  if (lat > n || lat < s || lng < w || lng > e) return null;
  const x = Math.floor(((lng - w) / (e - w)) * field.width);
  const y = Math.floor(((n - lat) / (n - s)) * field.height);
  if (x < 0 || x >= field.width || y < 0 || y >= field.height) return null;
  const idx = (y * field.width + x) * 4;
  const a = field.imgData.data[idx + 3];
  if (a < 128) return null;
  const u = ((field.imgData.data[idx] - 128) / 128) * WIND_MAX;
  const v = ((field.imgData.data[idx + 1] - 128) / 128) * WIND_MAX;
  return [u, v];
}

// Wave 20.49ak — Rider feedback on the live outdoor recording
// screen: wind particles were hard to see on the map, especially
// in calm conditions where dim-blue trails vanished into dark
// tiles. Recolored the ramp so:
//   calm      → near-white (255,245,200) at full opacity, pops
//               against green/gray Mapbox streets
//   moderate  → brand green (106,255,69)
//   strong    → warm red (255,90,60)
// Also bumped the line width in the caller so particles are
// physically thicker.
// Wave 20.49au — Per-map-style wind palette. Rider report:
// orange/warm colors vanish against every basemap that isn't
// the dark one (streets/outdoors/satellite are all mid-tone).
// Palette flips based on `theme` param passed to /embed/wind:
//   'dark'  → near-white → brand green → warm red (original)
//   'light' → deep navy → cobalt → magenta-red (higher
//             contrast against beige/green/photo backgrounds)
function speedColor(speed: number, theme: 'dark' | 'light'): string {
  const t = Math.min(1, speed / (WIND_MAX * 0.6));
  if (theme === 'light') {
    if (t < 0.33) {
      const k = t / 0.33;
      // deep navy → dark cobalt (dark cool anchor on any light map)
      return `rgba(${Math.round(15 + 5 * k)},${Math.round(30 + 30 * k)},${Math.round(90 + 50 * k)},${0.85 + 0.1 * k})`;
    } else if (t < 0.66) {
      const k = (t - 0.33) / 0.33;
      // cobalt → violet
      return `rgba(${Math.round(20 + 100 * k)},${Math.round(60 + 10 * k)},${Math.round(140 + 40 * k)},0.95)`;
    } else {
      const k = (t - 0.66) / 0.34;
      // violet → magenta-red
      return `rgba(${Math.round(120 + 100 * k)},${Math.round(70 - 50 * k)},${Math.round(180 - 100 * k)},0.95)`;
    }
  }
  // Dark theme (original palette)
  if (t < 0.33) {
    const k = t / 0.33;
    return `rgba(${Math.round(255)},${Math.round(245 - 20 * k)},${Math.round(200 - 40 * k)},${0.85 + 0.1 * k})`;
  } else if (t < 0.66) {
    const k = (t - 0.33) / 0.33;
    return `rgba(${Math.round(255 - 149 * k)},${Math.round(225 + 30 * k)},${Math.round(160 - 91 * k)},0.95)`;
  } else {
    const k = (t - 0.66) / 0.34;
    return `rgba(${Math.round(106 + 149 * k)},${Math.round(255 - 165 * k)},${Math.round(69 - 9 * k)},0.95)`;
  }
}

interface EmbedState {
  bbox: [number, number, number, number] | null;
  size: [number, number];
  jwt: string;
  hourOffset: number;
  // Wave 20.49au — Palette theme. 'dark' for dark basemaps,
  // 'light' for street / outdoors / satellite. Passed as `theme`
  // URL param and via `{ type: 'theme', theme: 'light' }` postMessage.
  theme: 'dark' | 'light';
  // Wave 20.44 — replay mode. When `replay` is populated the tick
  // loop iterates through pre-fetched frames on a wall-clock schedule
  // scaled to `rideDurationSec` (matches the algorithm the old Skia
  // WindParticles ran on mobile). When null the embed stays in
  // live-wind mode (unchanged behavior).
  replay: {
    frameTimestamps: string[];  // ISO-8601 UTC, one per hourly PNG
    rideSecs: number[];         // parallel array — seconds into the ride each frame represents
    rideDurationSec: number;
    loopWallSec: number;
    // Fixed size + bbox for the frame URLs, so hourly PNGs share
    // the same viewport regardless of what the base embed decides
    // for live-wind rendering.
    bbox: [number, number, number, number];
    size: string;
  } | null;
}

export default function WindEmbed() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stateRef = useRef<EmbedState>({ bbox: null, size: [300, 200], jwt: '', hourOffset: 0, theme: 'dark', replay: null });
  const fieldRef = useRef<Field | null>(null);
  const rafRef = useRef<number | null>(null);
  // Wave 20.44 — replay-mode loop state. Wall clock is when the
  // current loop iteration started; currentFrameIdx is the frame
  // whose field is currently loaded into fieldRef.
  const loopStartWallRef = useRef<number>(0);
  const currentFrameIdxRef = useRef<number>(-1);
  const loadingFrameIdxRef = useRef<number>(-1);
  // Wave 20.45 — replay frame cache. Prefetched fields keyed by idx
  // so the tick loop's frame swap is a memory read, not a fetch.
  // Without this every hourly boundary triggered a network round-
  // trip; particles would freeze on the old field for 200-800ms
  // then snap to the new one — the "jumpy" replay the user reported.
  const frameCacheRef = useRef<Map<number, Field>>(new Map());

  // CssBaseline paints a solid page background; force transparent
  // so the WebView underneath shows the Mapbox map.
  useEffect(() => {
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';
    document.body.style.margin = '0';
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
    };
  }, []);

  // URL params are the initial values. The parent WebView also
  // postMessages updates as the map camera moves.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bboxRaw = params.get('bbox');
    if (bboxRaw) {
      const parts = bboxRaw.split(',').map(Number);
      if (parts.length === 4 && parts.every(Number.isFinite)) {
        stateRef.current.bbox = parts as [number, number, number, number];
      }
    }
    const sizeRaw = params.get('size');
    if (sizeRaw) {
      const [w, h] = sizeRaw.split('x').map(Number);
      if (Number.isFinite(w) && Number.isFinite(h)) {
        stateRef.current.size = [w, h];
      }
    }
    const jwtParam = params.get('jwt');
    if (jwtParam) stateRef.current.jwt = jwtParam;
    else {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (stored) stateRef.current.jwt = stored;
    }
    const hourParam = params.get('hourOffset');
    if (hourParam) {
      const n = Number(hourParam);
      if (Number.isFinite(n)) stateRef.current.hourOffset = n;
    }
    // Wave 20.49au — palette theme param.
    const themeParam = params.get('theme');
    if (themeParam === 'light' || themeParam === 'dark') {
      stateRef.current.theme = themeParam;
    }
    // Wave 20.44 — replay params. `frames` is a comma-separated
    // list of ISO timestamps; `rideSecs` a parallel list of
    // seconds-into-ride; when both present + the ride length +
    // loop wall length are set, switch into replay mode.
    const framesRaw = params.get('frames');
    const rideSecsRaw = params.get('rideSecs');
    const rideDurationRaw = params.get('rideDurationSec');
    const loopWallRaw = params.get('loopWallSec');
    const replaySizeRaw = params.get('replaySize') || '384x192';
    if (
      framesRaw &&
      rideSecsRaw &&
      rideDurationRaw &&
      loopWallRaw &&
      stateRef.current.bbox
    ) {
      const frameTimestamps = framesRaw.split(',').filter(Boolean);
      const rideSecs = rideSecsRaw.split(',').map(Number).filter(Number.isFinite);
      const rideDurationSec = Number(rideDurationRaw);
      const loopWallSec = Number(loopWallRaw);
      if (
        frameTimestamps.length > 0 &&
        rideSecs.length === frameTimestamps.length &&
        Number.isFinite(rideDurationSec) &&
        rideDurationSec > 0 &&
        Number.isFinite(loopWallSec) &&
        loopWallSec > 0
      ) {
        stateRef.current.replay = {
          frameTimestamps,
          rideSecs,
          rideDurationSec,
          loopWallSec,
          bbox: stateRef.current.bbox,
          size: replaySizeRaw,
        };
        loopStartWallRef.current = Date.now();
        // Kick off parallel prefetch of every frame so the tick loop
        // never waits on a fetch. Bounded to 3 concurrent to avoid
        // hammering the weather backend when there are 30+ hourly
        // frames in a long ride.
        const bbox = stateRef.current.bbox;
        const jwt = stateRef.current.jwt;
        const size = replaySizeRaw;
        const [w, s, e, n] = bbox;
        const bboxStr = `${w.toFixed(3)},${s.toFixed(3)},${e.toFixed(3)},${n.toFixed(3)}`;
        let cursor = 0;
        const runOne = async (): Promise<void> => {
          while (cursor < frameTimestamps.length) {
            const idx = cursor++;
            if (frameCacheRef.current.has(idx)) continue;
            const url = `${WEATHER_API}/wind/png?bbox=${encodeURIComponent(bboxStr)}&t=${encodeURIComponent(frameTimestamps[idx])}&size=${size}`;
            const field = await decodeField(url, bbox, jwt);
            if (field) frameCacheRef.current.set(idx, field);
          }
        };
        Promise.all([runOne(), runOne(), runOne()]).catch(() => {});
      }
    }
  }, []);

  // Rebuild the URL + reload the field whenever bbox or hour changes.
  // In replay mode the tick loop drives frame loading via loadReplayFrame
  // instead — this is only the live-wind path.
  const loadFieldForCurrent = useCallback(async () => {
    if (stateRef.current.replay) return;
    const { bbox, jwt, hourOffset } = stateRef.current;
    if (!bbox) return;
    const [w, s, e, n] = bbox;
    const spanDeg = Math.max(e - w, n - s);
    const size = spanDeg > 60 ? '1024x512' : spanDeg > 20 ? '768x384' : '384x192';
    const t = new Date();
    t.setMinutes(0, 0, 0);
    t.setHours(t.getHours() + hourOffset);
    const url = `${WEATHER_API}/wind/png?bbox=${encodeURIComponent(`${w.toFixed(3)},${s.toFixed(3)},${e.toFixed(3)},${n.toFixed(3)}`)}&t=${encodeURIComponent(t.toISOString())}&size=${size}`;
    // Wave 20.49au — Retry the fetch a few times before giving
    // up. The wind API can transiently 401/503 during a cold
    // start (JWT not primed yet, WebView still spinning up) —
    // that used to fire a permanent-looking "Wind unavailable"
    // chip on the host. Now we quietly retry with a small
    // backoff and only surface the error once we're truly stuck.
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    let attempt = 0;
    let lastErr: string | null = null;
    while (attempt < 4) {
      try {
        const field = await decodeField(url, bbox, jwt);
        if (field) {
          fieldRef.current = field;
          try {
            (window as any).ReactNativeWebView?.postMessage?.(
              JSON.stringify({ type: 'field-loaded' }),
            );
          } catch { /* not in a RN WebView — ignore */ }
          return;
        }
        lastErr = 'no data';
      } catch (err: any) {
        lastErr = err?.message || 'fetch failed';
      }
      attempt++;
      // 1s, 2s, 4s, 8s backoff. Total ~15 s max, then error.
      await sleep(1000 * Math.pow(2, attempt - 1));
    }
    try {
      (window as any).ReactNativeWebView?.postMessage?.(
        JSON.stringify({ type: 'field-error', message: lastErr ?? 'unavailable' }),
      );
    } catch { /* ignore */ }
  }, []);

  // Wave 20.44 — replay-mode frame loader. Fetches the PNG for
  // frames[idx] and swaps fieldRef.current. Guarded against
  // concurrent loads so a quick scrub doesn't stack fetches.
  const loadReplayFrame = useCallback(async (idx: number) => {
    const replay = stateRef.current.replay;
    if (!replay || idx < 0 || idx >= replay.frameTimestamps.length) return;
    // Cache hit → synchronous swap, no jump.
    const cached = frameCacheRef.current.get(idx);
    if (cached) {
      fieldRef.current = cached;
      currentFrameIdxRef.current = idx;
      return;
    }
    if (loadingFrameIdxRef.current === idx) return;
    loadingFrameIdxRef.current = idx;
    const [w, s, e, n] = replay.bbox;
    const bboxStr = `${w.toFixed(3)},${s.toFixed(3)},${e.toFixed(3)},${n.toFixed(3)}`;
    const t = replay.frameTimestamps[idx];
    const url = `${WEATHER_API}/wind/png?bbox=${encodeURIComponent(bboxStr)}&t=${encodeURIComponent(t)}&size=${replay.size}`;
    const field = await decodeField(url, replay.bbox, stateRef.current.jwt);
    if (loadingFrameIdxRef.current !== idx) return;
    if (field) {
      frameCacheRef.current.set(idx, field);
      fieldRef.current = field;
      currentFrameIdxRef.current = idx;
    }
    loadingFrameIdxRef.current = -1;
  }, []);

  // Listen for updates from the parent WebView. Message shape:
  //   { type: 'bbox', bbox: [w,s,e,n] }
  //   { type: 'size', size: [w,h] }
  //   { type: 'jwt', jwt: '...' }
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      try {
        const raw = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (!raw || typeof raw !== 'object') return;
        if (raw.type === 'bbox' && Array.isArray(raw.bbox) && raw.bbox.length === 4) {
          stateRef.current.bbox = raw.bbox;
          loadFieldForCurrent();
        } else if (raw.type === 'size' && Array.isArray(raw.size) && raw.size.length === 2) {
          stateRef.current.size = raw.size;
          resizeCanvas();
        } else if (raw.type === 'jwt' && typeof raw.jwt === 'string') {
          stateRef.current.jwt = raw.jwt;
          loadFieldForCurrent();
        } else if (raw.type === 'hourOffset' && typeof raw.hourOffset === 'number') {
          stateRef.current.hourOffset = raw.hourOffset;
          loadFieldForCurrent();
        } else if (raw.type === 'theme' && (raw.theme === 'light' || raw.theme === 'dark')) {
          stateRef.current.theme = raw.theme;
          // No refetch — palette applies at draw time; the RAF
          // loop picks it up on the next frame.
        } else if (raw.type === 'refresh') {
          // Wave 20.49bw — Explicit "reload the field" ping from
          // the host. The last-drawn frame stays visible while
          // the new PNG loads (fieldRef only swaps on success),
          // so this feels like a smooth animation refresh
          // rather than a blackout.
          loadFieldForCurrent();
        }
      } catch {
        /* ignore malformed */
      }
    };
    // react-native-webview posts to the document, not window. Listen
    // on both so we work in a plain browser tab too (for debugging).
    window.addEventListener('message', onMessage);
    document.addEventListener('message', onMessage as any);
    return () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('message', onMessage as any);
    };
  }, [loadFieldForCurrent]);

  // Fetch the first field once we've read the initial state.
  useEffect(() => {
    loadFieldForCurrent();
    // Refresh every 10 min so the field tracks the hourly ingest.
    const id = window.setInterval(loadFieldForCurrent, 10 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [loadFieldForCurrent]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const [w, h] = stateRef.current.size;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    resizeCanvas();

    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * stateRef.current.size[0],
      y: Math.random() * stateRef.current.size[1],
      age: Math.random() * MAX_AGE,
    }));

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const tick = () => {
      const [w, h] = stateRef.current.size;
      // Fade layer for trails.
      ctx.globalCompositeOperation = 'destination-in';
      ctx.fillStyle = `rgba(0,0,0,${1 - FADE_ALPHA})`;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';

      // Wave 20.44 — replay-mode frame advance. Same algorithm as
      // the old Skia WindParticles: ride time = wall elapsed × ride
      // duration / loop wall duration; target frame = largest i
      // whose rideSec ≤ ride time. Loop wraps when wall elapsed
      // reaches loopWallSec.
      const replay = stateRef.current.replay;
      if (replay) {
        const nowMs = Date.now();
        const wallElapsed = (nowMs - loopStartWallRef.current) / 1000;
        let targetIdx = currentFrameIdxRef.current;
        if (replay.frameTimestamps.length === 1) {
          targetIdx = 0;
        } else if (wallElapsed >= replay.loopWallSec) {
          loopStartWallRef.current = nowMs;
          targetIdx = 0;
        } else {
          const rideT = (wallElapsed / replay.loopWallSec) * replay.rideDurationSec;
          targetIdx = 0;
          for (let i = 0; i < replay.rideSecs.length; i++) {
            if (replay.rideSecs[i] <= rideT) targetIdx = i;
            else break;
          }
        }
        if (targetIdx !== currentFrameIdxRef.current) {
          // Fire and forget — RAF keeps ticking with the previous
          // field until the new one lands.
          loadReplayFrame(targetIdx);
        }
      }

      const field = fieldRef.current;
      if (field) {
        const [fw, fs, fe, fn] = field.bbox;
        // Wave 20.49ak — thicker trails so the particle field
        // reads from a moving bike at arm's length. Was 1.4;
        // 2.4 lands well on both dark + street basemaps.
        // Wave 20.49cv — and zoom-aware on top: the field span is
        // a proxy for zoom; at ≥ 4° boost = 1, every halving of
        // the span adds 0.5 (1° = 2×, 0.25° = 3×), capped at 3.2.
        // Width and streak length both grow so a zoomed-in
        // route-planner loop reads as wind, not specks.
        const spanDeg = Math.max(fe - fw, fn - fs);
        const boost = spanDeg > 0 ? Math.max(1, Math.min(3.2, 1 + 0.5 * Math.log2(4 / spanDeg))) : 1;
        const speedBoost = 1 + 0.6 * (boost - 1);
        ctx.lineWidth = Math.max(2.4, 1.6 * boost);
        for (const p of particles) {
          p.age++;
          if (p.age > MAX_AGE) {
            p.x = Math.random() * w;
            p.y = Math.random() * h;
            p.age = 0;
            continue;
          }
          const lng = fw + (p.x / w) * (fe - fw);
          const lat = fn - (p.y / h) * (fn - fs);
          const uv = sampleField(field, lng, lat);
          if (!uv) {
            p.age = MAX_AGE + 1;
            continue;
          }
          const [u, v] = uv;
          const speed = Math.hypot(u, v);
          const scale = speed > 0 ? Math.pow(speed, SPEED_EXPONENT - 1) * SPEED_BASE * speedBoost : 0;
          const nextX = p.x + u * scale;
          const nextY = p.y - v * scale;
          ctx.strokeStyle = speedColor(speed, stateRef.current.theme);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(nextX, nextY);
          ctx.stroke();
          p.x = nextX;
          p.y = nextY;
          if (p.x < 0 || p.x > w || p.y < 0 || p.y > h) {
            p.age = MAX_AGE + 1;
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [resizeCanvas, loadReplayFrame]);

  return (
    <div
      style={{
        margin: 0,
        padding: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: 'transparent',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          background: 'transparent',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
